import React, { useContext } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import useMetric from '../../../lib/hooks/useMetric';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { ArrowForward } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import NutrientProfile from '../../../components/NutrientProfile';
import NutrientChanges from '../../../components/NutrientChanges';
import usePatientNutrientProfile from '../../../lib/hooks/usePatientNutrients';
import PatientActionBox from './PatientActionBox';
import PatientTipBox from './PatientTipBox';
import FoodGroupsBox from '../../../components/FoodGroupsBox';
import NutrientsBox from '../../../components/NutrientsBox';
import { NutrientsContextProvider } from '../../../lib/contexts/NutrientsContext';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    margin: 0,
    padding: 0,
    marginTop: 16,
  },
  topBar: {
    marginBottom: 16,
  },
  icon: theme.globalClasses.adminTableIcon,
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
  graphContainer: {
    // Add any existing styles for the graph container
  },
  '@media print': {
    graphContainer: {
      overflow: 'visible',
      transform: 'scale(0.50)',
      transformOrigin: 'top left',
      width: '100%',
      height: 'auto',
      position: 'relative',
    },
  },
  link: theme.globalClasses.link,
  boxContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const PatientKeyNutrientsBox = ({ summary = false, setSetSubtab }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);
  const { isLoading, data } = useMetric('priorities', currentTab.id);

  // Get workflow token from URL
  const workflowShareUrl = currentTab?.workflowShareUrl;
  const workflowToken = workflowShareUrl ? workflowShareUrl.split('#/s/')[1] : null;

  const { isLoading: nutrientsDataLoading, data: nutrientsData } = usePatientNutrientProfile(workflowToken);
  const { isLoading: checkinHistoryLoading, data: checkinHistoryData } = useMetric('checkin_history', currentTab.id);

  const topBarMargin = summary ? '12px' : '16px';

  // rules for display - if there are food changes to increase, we display what we can
  // otherwise don't display on summary and show message on key nutrients tab
  const hasData = !isLoading && data?.metric?.value?.food_changes?.increases?.length > 0;

  const HeaderComponent = () => (
    <Grid
      container
      item
      xs={12}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        width: '100%',
        maxWidth: '100%',
        mx: 0,
        padding: 0,
        marginBottom: topBarMargin,
      }}
    >
      {summary ? (
        <>
          {hasData && (
            <>
              <Typography
                variant={'overline'}
                sx={{ paddingBottom: 0 }}
              >
                Key Nutrients
              </Typography>
              <Typography
                variant={'tableHeadLink'}
                onClick={() => setSetSubtab('1')}
                sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0 }}
              >
                View Full Breakdown
                <IconButton sx={{ paddingLeft: '8px' }}>
                  <ArrowForward className={classes.icon} />
                </IconButton>
              </Typography>
            </>
          )}
        </>
      ) : (
        <Typography variant={'h2'}>Key Nutrient Breakdown</Typography>
      )}
    </Grid>
  );

  // Component definitions for reuse in both mobile and desktop views
  const NutrientProfileComponent = ({ maxWidth = 336 }) =>
    hasData &&
    nutrientsData.length > 0 && (
      <Box sx={{ minWidth: '260px', flexShrink: 0 }}>
        <NutrientProfile
          data={nutrientsData}
          maxWidth={maxWidth}
        />
      </Box>
    );

  const IncreasesComponent = () =>
    hasData && (
      <NutrientChanges
        data={data.metric.value?.food_changes?.increases}
        isIncrease={true}
        heading={'Increase These'}
        tip={'Start introducing more of these in your diet to improve your condition.'}
      />
    );

  const DecreasesComponent = () =>
    hasData &&
    data.metric.value?.food_changes?.decreases?.length > 0 && (
      <NutrientChanges
        data={data.metric.value?.food_changes?.decreases}
        isIncrease={false}
        heading={'Decrease These'}
        tip={'Decrease these foods to improve your condition.'}
      />
    );

  const ActionBoxComponent = ({ width = '336px' }) =>
    summary &&
    data.metric.value?.this_week_action && (
      <Box sx={width ? { width } : {}}>
        <PatientActionBox
          data={{
            description: data.metric.value?.this_week_action_description,
            action: data.metric.value?.this_week_action,
            checkins: checkinHistoryData?.metric?.value?.length,
          }}
        />
      </Box>
    );

  const TipBoxComponent = ({ width = '336px' }) =>
    summary &&
    data.metric.value?.todays_tip && (
      <Box sx={width ? { width } : {}}>
        <PatientTipBox
          data={{
            tip: data.metric.value?.todays_tip_description,
            description: data.metric.value?.todays_tip,
            resources: '',
          }}
        />
      </Box>
    );

  // Render content based on loading state and data availability
  const renderContent = () => {
    if (isLoading || nutrientsDataLoading || checkinHistoryLoading) {
      return <AdminProgressBar />;
    }

    if (!hasData && !summary) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            width: '100%',
          }}
        >
          <Typography variant="body1">No assessment data available.</Typography>
        </Box>
      );
    }

    return isMobile ? renderMobileView() : renderDesktopView();
  };

  // Mobile view layout
  const renderMobileView = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      }}
    >
      {/* Only show in summary view */}
      {summary && <NutrientProfileComponent maxWidth={336} />}
      {/* Keep existing components for both summary and full view */}
      <IncreasesComponent />
      <DecreasesComponent />
      {/* Only show in summary view */}
      {summary && (
        <>
          <ActionBoxComponent />
          <TipBoxComponent />
        </>
      )}
      {/* Only show in full view (not summary) */}
      {!summary && workflowToken && (
        <>
          {/* Food Groups Box */}
          <NutrientsContextProvider workflowToken={workflowToken}>
            <FoodGroupsBox workflowToken={workflowToken} />
          </NutrientsContextProvider>

          {/* Nutrients Boxes - all stacked vertically */}
          <NutrientsContextProvider workflowToken={workflowToken}>
            <NutrientsBox type="macronutrients" />
            <NutrientsBox type="nutrients" />
            <NutrientsBox type="fats" />
            <NutrientsBox type="minerals" />
            <NutrientsBox type="vitamins" />
          </NutrientsContextProvider>
        </>
      )}
    </Box>
  );

  // Desktop view layout
  const renderDesktopView = () => {
    if (!hasData) {
      // No data case for desktop
      return (
        summary && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
            <ActionBoxComponent />
            <TipBoxComponent />
          </Box>
        )
      );
    }

    if (summary) {
      return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '24px' }}>
          {/* Left column - Nutrient Profile */}
          <NutrientProfileComponent maxWidth={260} />

          {/* Right column - Increases/Decreases with Action/Tip underneath */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Increases and Decreases in a row */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
              <Box sx={{ width: '336px', flexShrink: 0 }}>
                <IncreasesComponent />
              </Box>
              <Box sx={{ width: '336px', flexShrink: 0 }}>
                <DecreasesComponent />
              </Box>
            </Box>

            {/* Action and Tip right underneath */}
            {hasData && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '24px',
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ width: '336px', flexShrink: 0 }}>
                  <ActionBoxComponent width="100%" />
                </Box>
                <Box sx={{ width: '336px', flexShrink: 0 }}>
                  <TipBoxComponent width="100%" />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      );
    } else {
      // Non-summary case
      return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Top row with Increases/Decreases in first column and FoodGroups in second */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
            {/* First column - Increases and Decreases stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '336px', flexShrink: 0 }}>
              <IncreasesComponent />
              <DecreasesComponent />
            </Box>

            {/* Second column - Food Groups */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {workflowToken && (
                <NutrientsContextProvider workflowToken={workflowToken}>
                  <FoodGroupsBox
                    workflowToken={workflowToken}
                    maxWidth={456}
                  />
                </NutrientsContextProvider>
              )}
            </Box>
          </Box>

          {/* Bottom row for nutrient boxes */}
          {workflowToken && (
            <NutrientsContextProvider workflowToken={workflowToken}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
                  <NutrientsBox type="macronutrients" />
                  <NutrientsBox type="nutrients" />
                  <NutrientsBox type="fats" />
                  <NutrientsBox type="minerals" />
                  <NutrientsBox type="vitamins" />
                </Box>
              </Box>
            </NutrientsContextProvider>
          )}
        </Box>
      );
    }
  };

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className={`patient-key-nutrients-box`}
          id="patient-key-nutrients-box"
        >
          <HeaderComponent />
          {renderContent()}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientKeyNutrientsBox;
